import {
  Box,
  Button,
  Card,
  Checkbox,
  colors,
  FormHelperText,
  FormControlLabel,
  Grid,
  styled,
  TextField,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorText from 'components/ErrorText/ErrorText';
import SnackbarContext from 'contexts/snackbarContext';
import {
  createOrganisation,
  getOrganisationById,
  modifyOrganisation,
} from 'services/organisation.service';
import { CreateOrganisationType } from 'types/organisations.type';

type CreateOrganisationFormType = {
  companyName: string;
  contactEmail: string;
  zipCode: string;
  city: string;
  address: string;
  groups?: string[];
  isOpen?: boolean;
  selfCreateGroups?: boolean;
};

const CustomForm = styled('form')`
  width: 100%;
`;

export default function CreateOrganizationForm() {
  const { t } = useTranslation();
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<CreateOrganisationFormType>();
  const { handleOpen } = useContext(SnackbarContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [groups, setCurrentGroups] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selfCreateGroups, setSelfCreateGroups] = useState<boolean>(false);
  const [companyLogo, setCompanyLogo] = useState<File | null>(null);
  const [companyLogoError, setCompanyLogoError] = useState<string | null>(null);
  const [existingLogoPath, setExistingLogoPath] = useState<string | null>(null);

  const getLogoUrl = (rawPath: string): string => {
    if (/^https?:\/\//i.test(rawPath)) {
      return rawPath;
    }

    const apiBase = (process.env.REACT_APP_API_BASE_URL ?? window.location.origin)
      .replace(/\/api\/?$/, '')
      .replace(/\/$/, '');

    const normalizedPath = rawPath
      .replace(/^\/+/, '')
      .replace(/^public\/+/, '');

    return `${apiBase}/public/${normalizedPath}`;
  };

  const existingLogoUrl = existingLogoPath ? getLogoUrl(existingLogoPath) : null;

  const validateCompanyLogo = async (file: File): Promise<string | null> => {
    if (file.type !== 'image/png') {
      return t('formErrors.companyLogoPngOnly');
    }

    const imageUrl = URL.createObjectURL(file);

    try {
      const dimensions = await new Promise<{ width: number; height: number }>(
        (resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = () => reject(new Error('Invalid image file'));
          img.src = imageUrl;
        },
      );

      const { width, height } = dimensions;
      if (width !== height || width < 320 || width > 360) {
        return t('formErrors.companyLogoDimensions');
      }

      return null;
    } catch {
      return t('formErrors.companyLogoInvalid');
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  };

  useEffect(() => {
    if (id) {
      getOrganisationById(id).then(({ data }) => {
        if (data.address) {
          const addressArray = data.address.split(',');
          setValue('companyName', data.companyName);
          setValue('contactEmail', data.contactEmail);
          setValue('city', addressArray[0].trim());
          setValue('zipCode', addressArray[1].trim());
          setValue('address', addressArray[2].trim());
          setIsOpen(data.isOpen);
          setSelfCreateGroups(data.selfCreateGroups);
          setExistingLogoPath(
            typeof data.logoPath === 'string' && data.logoPath.length > 0
              ? data.logoPath
              : null,
          );
        }
      });
    }
  }, [id]);

  const onSubmit = (data: CreateOrganisationFormType) => {
    void (async () => {
      if (!id && !companyLogo) {
        setCompanyLogoError(t('formErrors.companyLogoRequired'));
        return;
      }

      if (companyLogo) {
        const validationError = await validateCompanyLogo(companyLogo);
        if (validationError) {
          setCompanyLogoError(validationError);
          return;
        }
      }

      const emailHandler = data.contactEmail.split('@')[1];
      const orgObject: CreateOrganisationType = {
        companyName: data.companyName,
        contactEmail: data.contactEmail,
        emailHandler,
        address: `${data.zipCode}, ${data.city}, ${data.address}`,
        isOpen,
        selfCreateGroups,
      };

      if (groups.length) {
        orgObject.groups = groups;
      }

      if (companyLogo) {
        orgObject.companyLogo = companyLogo;
      }

      if (!id) {
        createOrganisation(orgObject)
          .then(() => {
            setCurrentGroups([]);
            navigate('/organisations');
            handleOpen(
              t('organisationsForm.orgCreatedSuccessfullyMessage'),
              'success',
            );
          })
          .catch(({ data }) => {
            handleOpen(data.message);
          });
      } else {
        modifyOrganisation(id, orgObject)
          .then(() => {
            navigate('/organisations');
            handleOpen(
              t('organisationsForm.orgModifiedSuccessfullyMessage'),
              'success',
            );
          })
          .catch(({ data }) => {
            handleOpen(data.message);
          });
      }
    })();
  };

  const checkKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  return (
    <Card sx={{ padding: '40px', maxWidth: '800px' }}>
      <CustomForm onSubmit={handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="companyName"
              defaultValue=""
              rules={{
                required: true,
                maxLength: 30,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('formFields.companyName')}
                  type="text"
                  inputProps={{ maxLength: 30 }}
                  error={errors.companyName ? true : false}
                />
              )}
            />
            {errors.companyName && (
              <ErrorText>{t('formErrors.companyNameRequired')}</ErrorText>
            )}
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="contactEmail"
              defaultValue=""
              rules={{
                required: true,
                pattern: /^\w+([.-]?\w+)*@\w+(-]?\w+)*(\.\w{2,3})+$/,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('formFields.contactEmail')}
                  type="text"
                  error={errors.contactEmail ? true : false}
                />
              )}
            />
            {errors.contactEmail && errors.contactEmail.type === 'required' && (
              <ErrorText>{t('formErrors.contactEmailRequired')}</ErrorText>
            )}
            {errors.contactEmail && errors.contactEmail.type === 'pattern' && (
              <ErrorText>{t('formErrors.contactEmailPattern')}</ErrorText>
            )}
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="zipCode"
              defaultValue=""
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('formFields.zipCode')}
                  type="text"
                  error={errors.zipCode ? true : false}
                />
              )}
            />
            {errors.zipCode && (
              <ErrorText>{t('formErrors.zipCodeRequired')}</ErrorText>
            )}
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="city"
              defaultValue=""
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('formFields.city')}
                  type="text"
                  error={errors.city ? true : false}
                />
              )}
            />
            {errors.city && (
              <ErrorText>{t('formErrors.cityRequired')}</ErrorText>
            )}
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="address"
              defaultValue=""
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('formFields.address')}
                  type="text"
                  error={errors.address ? true : false}
                />
              )}
            />
            {errors.address && (
              <ErrorText>{t('formErrors.addressRequired')}</ErrorText>
            )}
          </Grid>

          <Grid item xs={6}>
            <Controller
              control={control}
              name="isOpen"
              render={({ field }) => (
                <FormControlLabel
                  sx={{
                    border: '1px solid black',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    width: '100%',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    borderColor: colors.grey[400],
                    borderRadius: '5px',
                    color: colors.grey[700],
                    '&:hover': {
                      borderColor: 'black',
                    },
                  }}
                  control={
                    <Checkbox
                      {...field}
                      checked={isOpen}
                      value={isOpen}
                      onChange={() => setIsOpen((prev) => !prev)}
                    />
                  }
                  label={t('formFields.isOpen')}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="stretch">
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="selfCreateGroups"
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{
                        border: '1px solid black',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        width: '100%',
                        paddingTop: '6px',
                        paddingBottom: '6px',
                        borderColor: colors.grey[400],
                        borderRadius: '5px',
                        color: colors.grey[700],
                        '&:hover': {
                          borderColor: 'black',
                        },
                      }}
                      control={
                        <Checkbox
                          {...field}
                          checked={selfCreateGroups}
                          onChange={() => setSelfCreateGroups((prev) => !prev)}
                        />
                      }
                      label={t('formFields.selfCreateGroups')}
                    />
                  )}
                />

                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    type="file"
                    label={t('formFields.companyLogo')}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ accept: 'image/png' }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const file = event.target.files?.[0] ?? null;
                      setCompanyLogo(file);
                      setCompanyLogoError(null);
                    }}
                  />
                  <FormHelperText>{t('formFields.companyLogoHelper')}</FormHelperText>
                  {companyLogoError && <ErrorText>{companyLogoError}</ErrorText>}
                </Box>
              </Grid>

              {existingLogoUrl && (
                <Grid
                  item
                  xs={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    minHeight: '220px',
                  }}
                >
                  <a href={existingLogoUrl} target="_blank" rel="noopener noreferrer">
                    <Box
                      component="img"
                      src={existingLogoUrl}
                      alt={String(t('formFields.companyLogo'))}
                      sx={{
                        height: '140px',
                        width: 'auto',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        border: '1px solid',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        borderColor: colors.grey[300],
                        borderRadius: '5px',
                        px: 0.5,
                        cursor: 'zoom-in',
                      }}
                    />
                  </a>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Button variant="contained" sx={{ marginTop: '20px' }} type="submit">
          {t('formFields.submitButtonLabel')}
        </Button>
      </CustomForm>
    </Card>
  );
}
